import { useMemo } from "react";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Image } from "../../components/Image";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { Slider } from "../../components/Slider";
import { TableBody } from "../../components/Table/TableBody";
import { TableCell } from "../../components/Table/TableCell";
import { TableHead } from "../../components/Table/TableHead";
import { TableRow } from "../../components/Table/TableRow";
import {
  CheckStatusEnum,
  RiskLevelColors,
  RiskLevelEnum,
  RiskLevelIcons,
} from "../../context/MandateStatusContext";
import { useIndividualCheck } from "../../data/useIndividualCheck";
import { capitalise } from "../../utils/string";
import {
  CheckContainer,
  CheckHeading,
  CheckParagraph,
  CheckTable,
  CheckTableCell,
  CheckTableHeaderRow,
  CheckTitle,
  Section,
} from "./CheckStyledComponents";
import { ApprovalComment } from "./approvalComment";
import { CheckFields } from "./CheckFields";
import { CheckResults } from "./CheckResults";
import { IndividualChecksMap } from "./helper";
import { ManualAcceptReject } from "./ManualAcceptReject";

const normalizeRisk = (risk?: string | null) => {
  const value = risk?.toLowerCase();
  return value === "high" || value === "medium" || value === "low" ? value : "";
};

const getDisplayValue = (value: any) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "object") {
    return (
      value.name ?? value.label ?? value.title ?? value.code ?? value.id ?? "-"
    );
  }
  return String(value);
};

const getRisk = (item: any) =>
  normalizeRisk(
    item?.risk_level ??
      item?.risk ??
      item?.configured_risk_level ??
      item?.option?.risk_level,
  );

const getSelections = (instance: any, keys: string[]) => {
  for (const key of keys) {
    const value = instance?.[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const getNarrative = (instance: any) =>
  instance?.nature_of_business ??
  instance?.description ??
  instance?.narrative ??
  instance?.business_description ??
  instance?.details?.nature_of_business ??
  "";

const getRiskCounts = (items: any[]) =>
  items.reduce(
    (accumulator, item) => {
      const risk = getRisk(item) || "low";
      accumulator[risk] += 1;
      return accumulator;
    },
    { low: 0, medium: 0, high: 0 },
  );

const getRiskHighlight = (risk: string) => {
  if (risk === "high") return "#FFF4D4";
  if (risk === "medium") return "#FFF9E8";
  return "transparent";
};

const RiskBadge = ({ risk }: { risk?: string }) => {
  const level = normalizeRisk(risk);

  if (!level) {
    return <CheckParagraph>-</CheckParagraph>;
  }

  return (
    <Flex gap="8" alignItems="center">
      <CheckParagraph color={RiskLevelColors[level]}>
        {capitalise(RiskLevelEnum[level])}
      </CheckParagraph>
      <Image
        src={RiskLevelIcons[level]}
        alt={`${level} risk`}
        height="18px"
        width="18px"
      />
    </Flex>
  );
};

const RiskSummary = ({ title, items }: { title: string; items: any[] }) => {
  const riskCounts = getRiskCounts(items);

  return (
    <Box mb={3}>
      <H3 mb={3}>{title}</H3>

      <Flex gap="48" flexWrap="wrap">
        <div>
          <CheckHeading>Low risk</CheckHeading>
          <CheckParagraph>{riskCounts.low}</CheckParagraph>
        </div>
        <div>
          <CheckHeading>Medium risk</CheckHeading>
          <CheckParagraph>{riskCounts.medium}</CheckParagraph>
        </div>
        <div>
          <CheckHeading>High risk</CheckHeading>
          <CheckParagraph>{riskCounts.high}</CheckParagraph>
        </div>
      </Flex>
    </Box>
  );
};

const RiskTable = ({
  title,
  items,
  showCode = true,
}: {
  title: string;
  items: any[];
  showCode?: boolean;
}) => (
  <Box mb={4}>
    <H3>{title}</H3>

    {items.length === 0 ? (
      <Paragraph color="gray.60" fontSize={1}>
        No {title.toLowerCase()} submitted.
      </Paragraph>
    ) : (
      <CheckTable>
        <TableHead></TableHead>
        <TableBody>
          <CheckTableHeaderRow>
            <CheckTableCell>Name</CheckTableCell>
            {showCode && <CheckTableCell>Code</CheckTableCell>}
            <CheckTableCell>Risk level</CheckTableCell>
          </CheckTableHeaderRow>

          {items.map((item, index) => {
            const risk = getRisk(item) || "low";
            const riskHighlight = getRiskHighlight(risk);

            return (
              <TableRow key={item?.id ?? index}>
                <TableCell
                  width="58%"
                  fontWeight="500"
                  color="gray.80"
                  borderBottom="none"
                  style={{
                    background: riskHighlight,
                  }}
                >
                  {getDisplayValue(item)}
                </TableCell>
                {showCode && (
                  <TableCell
                    width="18%"
                    color="gray.60"
                    borderBottom="none"
                    style={{
                      background: riskHighlight,
                    }}
                  >
                    {item?.code || "-"}
                  </TableCell>
                )}
                <TableCell
                  color="gray.60"
                  borderBottom="none"
                  style={{
                    background: riskHighlight,
                  }}
                >
                  <RiskBadge risk={risk} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </CheckTable>
    )}
  </Box>
);

export const NatureOfBusinessCheck = (props: any) => {
  const { isOpen, onRequestClose, id, useSlider = true } = props;

  const { data: individualCheckData, isLoading: isLoadingIndividualCheck } =
    useIndividualCheck(id);

  const check = individualCheckData?.data;
  const instance: any = check?.instance;

  const industries = useMemo(
    () =>
      getSelections(instance, [
        "industries",
        "industry",
        "sic_industries",
        "selected_industries",
      ]),
    [instance],
  );
  const sectors = useMemo(
    () =>
      getSelections(instance, [
        "sectors",
        "sector",
        "business_sectors",
        "selected_sectors",
      ]),
    [instance],
  );
  const narrative = getNarrative(instance);
  const title =
    check?.title || IndividualChecksMap.nature_of_business_check.title;

  const content = (
    <CheckContainer>
      <Flex justifyContent="space-between">
        <CheckTitle>{title}</CheckTitle>

        {check?.status?.status === CheckStatusEnum.Review && (
          <ManualAcceptReject title={title} id={id} isMonitoring={false} />
        )}
      </Flex>

      {check?.status?.status && <CheckResults status={check.status.status} />}

      {instance?.approval_comment && (
        <ApprovalComment
          data={instance.approval_comment}
          status={check?.status?.status}
        />
      )}

      <CheckFields
        individual_checkfield_result={check?.individual_checkfield_result}
      />

      <Section>
        <Box mb={3}>
          <H3>Nature of business</H3>
          <Paragraph color="gray.60" fontSize={1} my={0}>
            {narrative || "No nature of business narrative submitted."}
          </Paragraph>
        </Box>

        <RiskSummary title="Industries" items={industries} />
        <RiskTable title="Industries" items={industries} />

        <RiskSummary title="Sectors" items={sectors} />
        <RiskTable title="Sectors" items={sectors} showCode={false} />
      </Section>
    </CheckContainer>
  );

  return (
    <>
      {isLoadingIndividualCheck ? (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      ) : useSlider ? (
        <Slider
          isOpen={isOpen}
          onRequestClose={() => onRequestClose(false)}
          width="70%"
        >
          {content}
        </Slider>
      ) : (
        content
      )}
    </>
  );
};
